import { hasSplashback, hasWorktop } from '@bulthaup/api/entities/project';
import { Button, Group, Stack, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { TRPCClientError } from '@trpc/client';

import { RouterOutput, trpc } from '../../../../../../services/trpc';
import DeliveryOrderForm, {
  DeliveryOrderFormValues,
} from './delivery-order-form';

function getMissingRequirements(
  project: NonNullable<RouterOutput['projects']['get']>,
) {
  const projectHasWorktop = hasWorktop(project);
  const projectHasSplashback = hasSplashback(project);
  const missingRequirements: string[] = [];

  if (!project.deliveryDate) {
    const message = "définissez d'abord une date de livraison pour le projet";
    missingRequirements.push(message);
  }

  if (projectHasWorktop && !project.isWorktopProviderNotified) {
    const message = 'le fournisseur plan de travail doit être notifié';
    missingRequirements.push(message);
  }

  if (projectHasSplashback && !project.isSplashbackProviderNotified) {
    missingRequirements.push('le fournisseur crédence doit être notifié');
  }

  if (!project.isInstallationFolderReady) {
    const message = 'le poseur / les artisans externes doivent être notifiés';
    missingRequirements.push(message);
  }

  return missingRequirements;
}

export function DeliveryOrderButton({
  project,
}: {
  project: NonNullable<RouterOutput['projects']['get']>;
}) {
  const { data: supplies } = trpc.projects.getSupplies.useQuery({
    projectId: project.id,
  });

  const utils = trpc.useUtils();
  const generateDeliveryOrderMutation =
    trpc.documents.generateDeliveryOrder.useMutation();

  const openDeliveryOrderModal = () => {
    modals.open({
      title: 'Ordre de livraison',
      size: 'xl',
      children: (
        <DeliveryOrderForm
          project={project}
          supplies={supplies || []}
          onCancel={() => modals.closeAll()}
          onSubmit={async (values: DeliveryOrderFormValues) => {
            try {
              await generateDeliveryOrderMutation.mutateAsync({
                projectId: project.id,
                selectedSupplies: values.selectedSupplies,
                access: values.access,
                deliveryTime: values.deliveryTime,
                deliveryContactUserId: values.deliveryContactUserId,
              });

              // Invalidate the project query to refetch updated data
              await utils.projects.get.invalidate({ slug: project.slug });

              notifications.show({
                title: 'Ordre de livraison créé',
                message: "L'ordre de livraison a été créé avec succès",
                color: 'green',
              });

              modals.closeAll();
            } catch (e: unknown) {
              console.log('Error creating delivery order:', e);
              notifications.show({
                title: 'Erreur',
                message:
                  e instanceof TRPCClientError
                    ? e.message
                    : "Erreur lors de la création de l'ordre de livraison",
                color: 'red',
              });
            }
          }}
        />
      ),
    });
  };

  const missingRequirements = getMissingRequirements(project);
  const disabledTooltipLabel = `Conditions requises avant de créer un ordre de livraison :\n${missingRequirements.map(requirement => `- ${requirement}`).join('\n')}`;
  const isDisabled = missingRequirements.length > 0;

  const button = (
    <Button
      variant="outline"
      leftSection={<IconPlus size={16} />}
      onClick={openDeliveryOrderModal}
      disabled={isDisabled}
    >
      Ajouter un ordre de livraison
    </Button>
  );

  return (
    <Stack>
      <Group>
        {isDisabled ? (
          <Tooltip
            multiline
            label={
              <Text style={{ whiteSpace: 'pre-line' }}>
                {disabledTooltipLabel}
              </Text>
            }
          >
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </Group>
    </Stack>
  );
}
